import Flickity from 'flickity';
import CustomScrollbar from '../features/custom-scrollbar';
import {isMobile} from '../util/media-query';

const selectors = {
  productSlideshow: '[data-product-slideshow]',
  productImage: '[data-product-single-media-wrapper]',
  productThumbs: '[data-product-single-media-thumbs]',
  productThumbsVertical: '[data-product-single-media-thumbs-vertical]',
  productThumb: '[data-thumbnail]',
  productThumbLink: '[data-thumbnail-id]',
  deferredMediaButton: '[data-deferred-media-button]',
  mediaType: 'data-type',
  id: 'data-id',
  tabIndex: 'tabindex',
  arrows: 'data-arrows',
  dots: 'data-dots',
  navSelected: '.is-nav-selected',
  scrollable: '[data-custom-scrollbar-items]',
};

const classes = {
  active: 'active',
  sliderEnabled: 'flickity-enabled',
  mediaHidden: 'media--hidden',
  focusEnabled: 'is-focused',
  thumbsArrows: 'product__images__slider-nav--arrows',
  isMoving: 'is-moving',
  navSelected: 'is-nav-selected',
};

const attributes = {
  thumbnail: 'data-thumbnail',
};

class InitSlider {
  constructor(section) {
    this.container = section.container;
    this.scrollable = this.container.querySelector(selectors.scrollable);
    this.slideshow = this.container.querySelector(selectors.productSlideshow);
    this.productImages = this.container.querySelectorAll(selectors.productImage);
    this.thumbs = this.container.querySelector(selectors.productThumbs);
    this.thumbsVertical = this.container.querySelector(selectors.productThumbsVertical);
    this.flkty = null;
    this.flktyNav = null;
    this.flickityOptionsNav = null;
    this.resizeEvent = () => this.resizeEvents();

    if (this.slideshow) {
      this.flickityOptionsNav = {
        asNavFor: this.slideshow,
        pageDots: false,
        prevNextButtons: true,
        arrowShape: theme.icons.arrowNavSlider,
        groupCells: true,
        contain: true,
      };
    }

    if (this.productImages.length > 1) {
      this.init();
    }
  }

  init() {
    this.createSlider();
    this.createSliderNav();
    this.createScrollable();

    document.addEventListener('theme:resize:width', this.resizeEvent);
  }

  resizeEvents() {
    this.initSliderNavVertical();

    this.checkThumbsWidth();

    this.createScrollable();
  }

  createSlider() {
    if (!this.slideshow) {
      return;
    }

    const instance = this;
    const firstSlide = this.slideshow.querySelectorAll(`[${selectors.mediaType}]`)[0];
    const arrows = this.slideshow.getAttribute(selectors.arrows) === 'true';
    const dots = this.slideshow.getAttribute(selectors.dots) === 'true';

    const flickityOptions = {
      autoPlay: false,
      arrowShape: theme.icons.arrowNavSlider,
      prevNextButtons: arrows,
      contain: true,
      pageDots: dots,
      adaptiveHeight: true,
      wrapAround: true,
    };

    this.flkty = new Flickity(this.slideshow, flickityOptions);

    if (firstSlide) {
      const firstType = firstSlide.getAttribute(selectors.mediaType);

      if (firstType === 'model' || firstType === 'video' || firstType === 'external_video') {
        this.flkty.options.draggable = false;
        this.flkty.updateDraggable();
      }
    }

    this.flkty.on('dragStart', function () {
      instance.slideshow.classList.add(classes.isMoving);
    });

    this.flkty.on('change', function (index) {
      const currentMedia = this.cells[index].element;
      const newMedia = this.selectedElement;
      const isMobileView = isMobile();

      currentMedia.dispatchEvent(new CustomEvent('theme:media:hidden'));
      newMedia.classList.remove(classes.mediaHidden);

      if (instance.thumbsVertical && !isMobileView) {
        const selectedThumb = instance.thumbsVertical.querySelector(selectors.navSelected);
        const newSelectedThumb = instance.thumbsVertical.querySelector(`[${attributes.thumbnail}="${index}"]`);

        if (selectedThumb) {
          selectedThumb.classList.remove(classes.navSelected);
        }

        if (newSelectedThumb) {
          newSelectedThumb.classList.add(classes.navSelected);
        }

        instance.scrollToThumb();
      }
    });

    this.flkty.on('settle', function () {
      const currentMedia = this.selectedElement;
      const mediaType = currentMedia.getAttribute(selectors.mediaType);

      if (mediaType === 'model' || mediaType === 'video' || mediaType === 'external_video') {
        // first boolean sets value, second option false to prevent refresh
        instance.flkty.options.draggable = false;
        instance.flkty.updateDraggable();
      } else {
        instance.flkty.options.draggable = true;
        instance.flkty.updateDraggable();
      }

      instance.switchMedia(currentMedia);
      instance.slideshow.classList.remove(classes.isMoving);
    });
  }

  createSliderNav() {
    const thumbs = this.thumbs || this.thumbsVertical;
    if (!thumbs || !this.slideshow) return;

    if (this.thumbsVertical) {
      this.initSliderNavVertical();
    } else {
      this.flktyNav = new Flickity(thumbs, this.flickityOptionsNav);
    }

    this.checkThumbsWidth();

    thumbs.querySelectorAll(selectors.productThumbLink).forEach((thumbLink, index) => {
      const thumbParent = thumbLink.closest(selectors.productThumb);

      if (this.thumbsVertical && thumbParent && index === 0) {
        thumbParent.classList.add(classes.navSelected);
      }

      thumbLink.addEventListener('click', (e) => {
        e.preventDefault();
        const isMobileView = isMobile();
        if (this.thumbsVertical && thumbParent && !isMobileView && this.flkty !== null) {
          this.flkty.selectCell(parseInt(thumbParent.getAttribute(attributes.thumbnail)));
        }
      });
    });
  }

  initSliderNavVertical() {
    if (!this.thumbsVertical || !this.slideshow) return;

    const isMobileView = isMobile();

    if (isMobileView && !this.flktyNav) {
      this.flktyNav = new Flickity(this.thumbsVertical, this.flickityOptionsNav);
    } else if (!isMobileView && this.flktyNav) {
      this.flktyNav.destroy();
      this.flktyNav = null;
    }
  }

  scrollToThumb() {
    const thumbs = this.thumbsVertical;

    if (thumbs) {
      const thumb = thumbs.querySelector(selectors.navSelected);
      if (!thumb) return;
      const thumbsScrollTop = thumbs.scrollTop;
      const thumbsScrollLeft = thumbs.scrollLeft;
      const thumbsWidth = thumbs.offsetWidth;
      const thumbsHeight = thumbs.offsetHeight;
      const thumbsPositionBottom = thumbsScrollTop + thumbsHeight;
      const thumbsPositionRight = thumbsScrollLeft + thumbsWidth;
      const thumbPosTop = thumb.offsetTop;
      const thumbPosLeft = thumb.offsetLeft;
      const thumbWidth = thumb.offsetWidth;
      const thumbHeight = thumb.offsetHeight;
      const thumbRightPos = thumbPosLeft + thumbWidth;
      const thumbBottomPos = thumbPosTop + thumbHeight;
      const topCheck = thumbsScrollTop > thumbPosTop;
      const bottomCheck = thumbBottomPos > thumbsPositionBottom;
      const leftCheck = thumbsScrollLeft > thumbPosLeft;
      const rightCheck = thumbRightPos > thumbsPositionRight;
      const verticalCheck = bottomCheck || topCheck;
      const horizontalCheck = rightCheck || leftCheck;
      const isMobileView = isMobile();

      if (verticalCheck || horizontalCheck) {
        let scrollTopPosition = thumbPosTop - thumbsHeight + thumbHeight;
        let scrollLeftPosition = thumbPosLeft - thumbsWidth + thumbWidth;

        if (topCheck) {
          scrollTopPosition = thumbPosTop;
        }

        if (rightCheck && isMobileView) {
          scrollLeftPosition += parseInt(window.getComputedStyle(thumbs).paddingRight);
        }

        if (leftCheck) {
          scrollLeftPosition = thumbPosLeft;

          if (isMobileView) {
            scrollLeftPosition -= parseInt(window.getComputedStyle(thumbs).paddingLeft);
          }
        }

        thumbs.scrollTo({
          top: scrollTopPosition,
          left: scrollLeftPosition,
          behavior: 'smooth',
        });
      }
    }
  }

  checkThumbsWidth() {
    if (this.thumbs || (this.thumbsVertical && isMobile())) {
      const thumbsHolder = this.thumbs || this.thumbsVertical;
      const thumbs = thumbsHolder.querySelectorAll(selectors.productThumb);
      const thumbsContainerPadding = parseInt(window.getComputedStyle(thumbsHolder).paddingLeft.replace('px', '')) * 2;
      const thumbsContainerWidth = thumbsHolder.offsetWidth - thumbsContainerPadding;
      let thumbsWidth = 0;

      thumbs.forEach((thumb) => {
        thumbsWidth += thumb.offsetWidth;
      });

      if (thumbsContainerWidth < thumbsWidth) {
        thumbsHolder.classList.add(classes.thumbsArrows);
      } else {
        thumbsHolder.classList.remove(classes.thumbsArrows);
      }

      if (this.flktyNav !== null && typeof this.flktyNav == 'object') {
        this.flktyNav.resize();
      }
    }
  }

  createScrollable() {
    if (!this.scrollable) return;

    if (isMobile()) {
      this.customScrollbar = new CustomScrollbar(this.container);
    } else if (this.customScrollbar) {
      this.customScrollbar.destroy();
    }
  }

  switchMedia(currentMedia) {
    const otherMedia = Array.prototype.filter.call(currentMedia.parentNode.children, function (child) {
      return child !== currentMedia;
    });
    const isFocusEnabled = document.body.classList.contains(classes.focusEnabled);

    if (isFocusEnabled) {
      currentMedia.dispatchEvent(new Event('focus'));
    }

    if (otherMedia.length) {
      otherMedia.forEach((element) => {
        element.classList.add(classes.mediaHidden);
        element.dispatchEvent(new CustomEvent('theme:media:hidden'));
      });
    }

    currentMedia.classList.remove(classes.mediaHidden);
    currentMedia.dispatchEvent(new CustomEvent('theme:media:visible'));

    // Force media loading if slide becomes visible
    const deferredMedia = currentMedia.querySelector('deferred-media');
    if (deferredMedia && deferredMedia.getAttribute('loaded') !== true) {
      currentMedia.querySelector(selectors.deferredMediaButton).dispatchEvent(new Event('click', {bubbles: false}));
    }
  }

  unload() {
    document.removeEventListener('theme:resize:width', this.resizeEvent);
  }
}

export default InitSlider;
